import {Component, Emitter, Object3D} from '@wonderlandengine/api';
import {property} from '@wonderlandengine/api/decorators.js';
import { SubmenuDivHandler } from './SubmenuDivHandler.js';
import { VrMenuRenderer } from './renderers/VrMenuRenderer.js';
import { DeviceSubmenuDH } from './DeviceSubmenuDH.js';
import { VidshareSubmenuDH } from './VidshareSubmenuDH.js';
import { IntifaceController } from '../IntifaceController.js';
import { RemoteDeviceConnSubmenuDH } from './RemoteDeviceConnSubmenuDH.js';
import { EToySubmenuDH } from './EToySubmenuDH.js';
import { SceneSubmenuDH } from './SceneSubmenuDH.js';
import { SettingsSubmenuDH } from './SettingsSubmenuDH.js';
import { AccountSubmenuDH } from './AccountSubmenuDH.js';
import { EntertainersSubmenuDH } from './EntertainersSubmenuDH.js';
import { RoomsSubmenuDH } from './RoomsSubmenuDH.js';
import { UserAdminSubmenuDH } from './UserAdminSubmenuDH.js';
import { AvatarSubmenuDH } from './AvatarSubmenuDH.js';
import { EventsClient } from '../events-client.js';
import Logger from '../utils/logger.js';
import { UserMessaging } from '../utils/user-messaging.js';
import { createVrMainMenuSchema } from './schemas/vr-main-menu-schema.js';
import { isSchemaVisibleOnPlatform, type MenuSchema } from './MenuSchema.js';
import { createDeviceMenuSchema } from './schemas/device-menu-schema.js';
import { createAccountMenuSchema } from './schemas/account-menu-schema.js';
import { createSettingsMenuSchema } from './schemas/settings-menu-schema.js';
import { createRoomsMenuSchema } from './schemas/rooms-menu-schema.js';
import { createUserAdminMenuSchema } from './schemas/user-admin-menu-schema.js';
import { createEToyMenuSchema } from './schemas/etoy-menu-schema.js';
import { createSceneMenuSchema } from './schemas/scene-menu-schema.js';
import { createRemoteDeviceConnMenuSchema } from './schemas/remote-device-conn-menu-schema.js';
import { createEntertainersMenuSchema } from './schemas/entertainers-menu-schema.js';
import { createAvatarMenuSchema } from './schemas/avatar-menu-schema.js';
import { getAvatarEntryById } from '../avatar/avatar-catalog.js';
import { AvatarController } from '../avatar/avatar-controller.js';
import { setComponentsActive } from '../utils/activate-children.js';

const MODULE = 'MenuController';

export enum Submenu {
  Main = 'MAIN',
  Device = 'DEVICE',
    Avatar = 'AVATAR',
  RemoteDeviceConn = 'REMOTEDEVICECONN',
    RemoteDeviceClone = 'REMOTEDEVICECLONE', //there may be multiple instances of this submenu
        EToy = 'ETOY',
        Scene = 'SCENE',
        Settings = 'SETTINGS',
        Account = 'ACCOUNT',
        Entertainers = 'ENTERTAINERS',
        Rooms = 'ROOMS',
        UserAdmin = 'USERADMIN'
}

const CSS = `
        /* Base responsive styles for all devices */
        * {
            box-sizing: border-box;
        }

        /* Custom Scrollbar Styling */
        .menu-modal-holder::-webkit-scrollbar,
        .menu-modal-content-holder::-webkit-scrollbar {
            width: 12px;
        }

        .menu-modal-holder::-webkit-scrollbar-track,
        .menu-modal-content-holder::-webkit-scrollbar-track {
            background: rgba(10, 15, 30, 0.8);
            border-radius: 10px;
            border: 1px solid rgba(0, 217, 255, 0.2);
        }

        .menu-modal-holder::-webkit-scrollbar-thumb,
        .menu-modal-content-holder::-webkit-scrollbar-thumb {
            background: linear-gradient(180deg, #00d9ff 0%, #00a8cc 100%);
            border-radius: 10px;
            border: 1px solid rgba(0, 255, 255, 0.3);
            box-shadow: 0 0 10px rgba(0, 217, 255, 0.4);
        }

        .menu-modal-holder::-webkit-scrollbar-thumb:hover,
        .menu-modal-content-holder::-webkit-scrollbar-thumb:hover {
            background: linear-gradient(180deg, #00ffff 0%, #00d9ff 100%);
            box-shadow: 0 0 15px rgba(0, 255, 255, 0.6);
        }

        /* Firefox scrollbar */
        .menu-modal-holder,
        .menu-modal-content-holder {
            scrollbar-width: thin;
            scrollbar-color: #00d9ff rgba(10, 15, 30, 0.8);
        }

        /* Style for the menu modal container */
        .menu-modal-container {
            font-family: 'Orbitron', 'Rajdhani', 'Exo 2', -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, monospace, sans-serif;
            display: none;
            justify-content: center;
            align-items: center;
            position: fixed;
            top: 0;
            left: 0;
            width: 100%;
            height: 100%;
            background: rgba(8, 12, 20, 0.12);
            backdrop-filter: none;
            z-index: 1000;
            padding: 1rem;
            overflow-y: auto;
        }

        /* Style for the menu modal content holder */
        .menu-modal-holder {
            background: linear-gradient(135deg, #0a0f1e 0%, #121829 50%, #0d1220 100%);
            border: 2px solid #00d9ff;
            border-radius: 16px;
            padding: 0;
            box-shadow: 0 0 40px rgba(0, 217, 255, 0.3), 0 0 80px rgba(0, 255, 157, 0.15), inset 0 1px 0 rgba(255, 255, 255, 0.1);
            text-align: center;
            margin: 0;
            position: relative;
            width: 50vw;
            max-width: 50vw;
            max-height: 85vh;
            overflow-y: auto;
        }

        /* Style for the menu modal content */
        .menu-modal-content {
            background: rgba(15, 20, 35, 0.6);
            border-radius: 12px;
            padding: clamp(0.25rem, 1vw, 0.5rem);
            text-align: center;
            margin: 0;
            position: relative;
            width: 100%;
            color: #e0f7ff;
        }

        .menu-modal-content h2 {
            font-size: clamp(1.25rem, 4vw, 1.75rem);
            margin: 0.25rem 0;
            color: #00d9ff;
            text-transform: uppercase;
            letter-spacing: 0.15em;
            text-shadow: 0 0 10px rgba(0, 217, 255, 0.7), 0 0 20px rgba(0, 217, 255, 0.4);
            font-weight: 700;
        }

        .menu-modal-content p {
            font-size: clamp(0.875rem, 2.5vw, 1rem);
            line-height: 1.5;
            color: #b8e3f0;
        }

        .menu-modal-content-holder {
            width: 100%;
            overflow-x: hidden;
        }

        /* Style for the navigation header of the modal */
        .menu-modal-navigation {
            background: linear-gradient(135deg, #0d1526 0%, #1a2235 100%);
            border-bottom: 2px solid #00d9ff;
            border-radius: 12px 12px 0 0;
            padding: clamp(0.25rem, 1vw, 0.5rem);
            box-shadow: 0 4px 12px rgba(0, 0, 0, 0.5), inset 0 1px 0 rgba(0, 217, 255, 0.2);
            text-align: left;
            margin: 0;
            position: relative;
            display: flex;
            flex-wrap: wrap;
            gap: 0.25rem;
            align-items: center;
            width: 100%;
        }

        /* Style for the submit button */
        .mm-submit-button {
            background: linear-gradient(135deg, #00a8cc 0%, #00d9ff 100%);
            color: #0a0f1e;
            border: 1px solid #00ffff;
            border-radius: 8px;
            margin: 0.125rem;
            padding: clamp(0.375rem, 1.5vw, 0.5rem) clamp(0.75rem, 2.5vw, 1rem);
            font-size: clamp(0.875rem, 2.5vw, 1rem);
            font-weight: 600;
            cursor: pointer;
            transition: all 0.3s ease;
            min-height: 44px;
            touch-action: manipulation;
            display: inline-flex;
            align-items: center;
            justify-content: center;
            text-align: center;
            text-transform: uppercase;
            letter-spacing: 0.05em;
            box-shadow: 0 0 15px rgba(0, 217, 255, 0.4), inset 0 1px 0 rgba(255, 255, 255, 0.3);
        }

        .mm-submit-button:hover {
            background: linear-gradient(135deg, #00ffff 0%, #00ff9d 100%);
            box-shadow: 0 0 25px rgba(0, 255, 255, 0.6), 0 0 40px rgba(0, 255, 157, 0.4), inset 0 1px 0 rgba(255, 255, 255, 0.5);
            transform: translateY(-2px);
        }

        .mm-submit-button:active {
            transform: translateY(0) scale(0.98);
            box-shadow: 0 0 10px rgba(0, 217, 255, 0.5), inset 0 1px 3px rgba(0, 0, 0, 0.3);
        }

        .mm-closex-button {
            position: absolute;
            top: 0.25rem;
            right: 0.25rem;
            background: linear-gradient(135deg, rgba(255, 80, 120, 0.3) 0%, rgba(255, 100, 140, 0.3) 100%);
            color: #ff5588;
            border: 1px solid rgba(255, 80, 120, 0.6);
            border-radius: 6px;
            padding: clamp(0.375rem, 1.5vw, 0.5rem) clamp(0.625rem, 2vw, 0.75rem);
            font-size: clamp(0.875rem, 2.5vw, 1rem);
            font-weight: bold;
            cursor: pointer;
            transition: all 0.3s ease;
            min-height: 36px;
            min-width: 36px;
            z-index: 10;
            touch-action: manipulation;
            box-shadow: 0 0 10px rgba(255, 80, 120, 0.2);
        }

        .mm-closex-button:hover {
            background: linear-gradient(135deg, rgba(255, 80, 120, 0.5) 0%, rgba(255, 100, 140, 0.5) 100%);
            color: #ffaabb;
            border-color: rgba(255, 80, 120, 0.8);
            box-shadow: 0 0 20px rgba(255, 80, 120, 0.4);
            transform: scale(1.05);
        }

        .mm-nav-button {
            background: rgba(0, 217, 255, 0.1);
            color: #00ffff;
            border: 1px solid #00d9ff;
            border-radius: 6px;
            padding: clamp(0.375rem, 1.5vw, 0.5rem) clamp(0.75rem, 2.5vw, 1rem);
            font-size: clamp(0.75rem, 2vw, 0.875rem);
            font-weight: 600;
            cursor: pointer;
            transition: all 0.3s ease;
            min-height: 40px;
            white-space: nowrap;
            touch-action: manipulation;
            text-transform: uppercase;
            letter-spacing: 0.08em;
            box-shadow: 0 0 10px rgba(0, 217, 255, 0.2), inset 0 1px 0 rgba(255, 255, 255, 0.1);
        }

        .mm-nav-button:hover {
            background: linear-gradient(135deg, #00d9ff 0%, #00ff9d 100%);
            color: #0a0f1e;
            border-color: #00ffff;
            box-shadow: 0 0 20px rgba(0, 217, 255, 0.5), 0 0 30px rgba(0, 255, 157, 0.3);
            transform: translateY(-2px);
        }

        .mm-nav-button:active {
            transform: translateY(0) scale(0.96);
        }

        /* Mobile-specific optimizations */
        @media (max-width: 768px) {
            .menu-modal-container {
                padding: 0.5rem;
                align-items: flex-start;
            }

            .menu-modal-holder {
                width: 95vw;
                max-width: 95vw;
                max-height: 90vh;
                margin-top: 1rem;
            }

            .menu-modal-navigation {
                flex-direction: column;
                align-items: stretch;
            }

            .mm-nav-button {
                width: 100%;
                text-align: center;
            }

            .mm-submit-button {
                width: 100%;
                max-width: none;
            }
        }

        /* Landscape mobile adjustments */
        @media (max-height: 600px) and (orientation: landscape) {
            .menu-modal-container {
                padding: 0.25rem;
            }

            .menu-modal-holder {
                max-height: 95vh;
            }

            .menu-modal-content h2 {
                font-size: 1.25rem;
                margin: 0.25rem 0;
            }

            .menu-modal-navigation {
                padding: 0.375rem;
            }
        }

        /* Tablet and desktop - no width override needed, uses base 50vw */
        @media (min-width: 769px) {
            .menu-modal-navigation {
                flex-direction: row;
            }
        }

        /* Large screens - even narrower */
        @media (min-width: 1200px) {
            .menu-modal-holder {
                width: 35vw;
                max-width: 35vw;
            }
        }
`;

const MM_NAVIGATION_HTML = `
        <!-- Navigation Modal -->
        <div class="menu-modal-holder">
            <div id="menu-modal-nav-content" class="menu-modal-content-holder">
                <div id="menu-modal-navigation" class="menu-modal-navigation">
                    <button id="mm-vidshare-closex-button" class="mm-closex-button">X</button>
                    <button id="mm-nav-main-button" class="mm-nav-button">Main</button>
                    <button id="mm-nav-device-button" class="mm-nav-button">Devices</button>
                    <button id="mm-nav-avatar-button" class="mm-nav-button">Avatar</button>
                    <button id="mm-nav-remotecon-button" class="mm-nav-button">Remote Devices</button>
                    <button id="mm-nav-etoy-button" class="mm-nav-button">EToys</button>
                    <button id="mm-nav-scene-button" class="mm-nav-button">Scene</button>
                    <button id="mm-nav-settings-button" class="mm-nav-button">Settings</button>
                    <button id="mm-nav-account-button" class="mm-nav-button">Account</button>
                    <button id="mm-nav-entertainers-button" class="mm-nav-button">Entertainers</button>
                    <button id="mm-nav-useradmin-button" class="mm-nav-button" style="display:none;">User Admin</button>
                    <button id="mm-nav-rooms-button" class="mm-nav-button">Rooms</button>
                </div>
                <div id="menu-modal-content-container" class="menu-modal-content-holder">
                </div>
                <div id="menu-modal-footer" class="menu-modal-content-holder">
                    <div id="menu-modal-footer-text" class="menu-modal-content">Multi-User Vidshare & Device Control</div>
                </div>
            </div>
        </div>
`;

/**
 * menu-controller
 */
export class MenuController extends Component {
    static TypeName = 'menu-controller';

    /**
     * The "MenuMesh" WLE object that hosts the VrMenuRenderer component.
     * Assign this in the editor.  Used in Phase 1 to show/hide the in-world
     * VR panel instead of exiting the XR session.
     */
    @property.object()
    vrMenuObject!: Object3D | null;

    @property.object()
    avatarPreviewPane!: Object3D | null;

    @property.object()
    avatarPreviewPaneVR!: Object3D | null;

    currentSubmenu: Submenu = Submenu.Main;

    modal!: HTMLDivElement;
    modalContent!: HTMLDivElement;
    footerMessageDiv!: HTMLDivElement;
    private vrMenuRenderer: VrMenuRenderer | null = null;
    private readonly onPreAuthStateChanged = () => {
        this.syncVrPreAuthMenuVisibility();
    };

    divHandlerMap: Map<Submenu, SubmenuDivHandler> = new Map<Submenu, SubmenuDivHandler>();
    remoteDeviceNavButtons: Map<number, HTMLInputElement> = new Map<number, HTMLInputElement>();

    sharePreviewVideo!: HTMLVideoElement;

    onUserChoice: Emitter<[string, object?]> = new Emitter<[string, object?]>();

    inXR = false;
    wasInXR = false;
    latestXRSession: XRSession | null = null;
    latestXRBlendMode: string | null = null;
    ignoreButtonInput: boolean = false;
    private xrMenuButtonWasPressed: boolean = false;
    private avatarPreviewInstance: Object3D | null = null;

    // Singleton
    private static _instance: MenuController;
    static get instance(): MenuController {
        return MenuController._instance;
    }

    init() {
        //Initialize singleton
        if (MenuController._instance) {
            Logger.error(MODULE, 'There can only be one instance of MenuController Component');
        } else {
            MenuController._instance = this;

            UserMessaging.instance.registerMenuController(this);

            const style = document.createElement('style');
            style.innerText = CSS;
            document.head.append(style);

            this.sharePreviewVideo = document.createElement('video') as HTMLVideoElement;
            this.sharePreviewVideo.classList.add('mm-preview-video'); 
            this.sharePreviewVideo.muted = true;
            this.sharePreviewVideo.volume = 0;
            this.sharePreviewVideo.autoplay = true;
            this.sharePreviewVideo.playsInline = true;

            this.modal = document.createElement('div');
            this.modal.classList.add('menu-modal-container');
            this.modal.id = 'menu-modal-container';
            this.modal.innerHTML = MM_NAVIGATION_HTML;

            document.body.appendChild(this.modal);

            this.modalContent = document.getElementById('menu-modal-content-container') as HTMLDivElement;
            this.modalContent.style.display = 'flex';

            this.vrMenuRenderer = this.resolveVrMenuRenderer();
        }
    }

    start() {
        window.addEventListener("keydown", (event) => {
            if(!this.ignoreButtonInput) {
                if (event.key === "m") {
                    this.displayMenu();
                }
            }
        });

        this.engine.onXRSessionStart.add(this.onXRSessionStart.bind(this));
        this.engine.onXRSessionEnd.add(this.onXRSessionEnd.bind(this));

        // Subscribe to IntifaceController state change events
        IntifaceController.instance.onDevicesChanged.add(() => {
            this.updateDeviceControls();
        });

        IntifaceController.instance.onConnectionStateChanged.add(() => {
            this.updateConditionals();
            this.updateDeviceControls();
        });

        IntifaceController.instance.onControlModeChanged.add(() => {
            this.updateConditionals();
            this.updateDeviceControls();
        });

        IntifaceController.instance.onRemotePeerChanged.add((peerKey: string, peerId: number | undefined, connected: boolean) => {
            if (connected && peerId !== undefined) {
                // Remote peer connected - switch to their device view using numeric peer ID and show dynamic nav
                this.switchMenuContent(Submenu.RemoteDeviceClone, peerId.toString());
                this.displayDynamicNavElements();
            } else {
                // Remote peer disconnected - remove dynamic submenu and navigation
                this.removeDynamicSubmenu(peerKey, peerId);
            }
        });

        IntifaceController.instance.onError.add((errorMessage: string) => {
            UserMessaging.instance.notify(`Error: ${errorMessage}`);
        });

        EventsClient.instanceOrNull?.onPreAuthStateChanged.add(this.onPreAuthStateChanged);
    }

    onXRSessionStart (session : XRSession) {
        this.inXR = true;
        this.latestXRSession = session;
        this.latestXRBlendMode = session.environmentBlendMode;

        // Always start XR with the in-world menu hidden.
        this.hideVrMenu();

        // If XR is re-entered via browser UI while the HTML menu is still open,
        // force-close it so controller-driven menu open continues to work.
        if (this.modal?.style.display === 'flex') {
            this.hideMenu(false);
        }

        this.syncVrPreAuthMenuVisibility();
    }

    onXRSessionEnd () {
        this.inXR = false;
        this.latestXRSession = null;
        this.xrMenuButtonWasPressed = false;

        this.hideVrMenu();
    }

    updateConditionals () {
        this.divHandlerMap.get(this.currentSubmenu)?.updateConditionals();
    }

    /**
     * Switches menu view to a different submenu.
     * Convenience method that hides current submenu and displays RemoteDeviceClone.
     * 
     * @param sub - Target submenu (currently unused, always switches to RemoteDeviceClone)
     * @param submenuKey - Remote peer key or ID for RemoteDeviceClone identification
     */
    public switchMenuContent(sub: Submenu, submenuKey?: string) {
        this.hideMenuContent(this.currentSubmenu);
        this.displayMenuContent(Submenu.RemoteDeviceClone, true, submenuKey);    
    }

    /**
     * Displays a specific submenu, creating handler instance and rendering content.
     * Manages submenu lifecycle: instantiation, caching, and DOM rendering.
     * 
     * @param sub - Submenu type to display (Main, Device, RemoteDeviceConn, RemoteDeviceClone)
     * @param force - Force redisplay even if already showing this submenu (for refresh)
     * @param submenuKey - Optional key for RemoteDeviceClone (peer ID or negotiation key)
     * 
     * @remarks
     * Submenu types:
     * - Main: Video sharing controls (VidshareSubmenuDH)
     * - Device: Local haptic device controls (DeviceSubmenuDH)
     * - RemoteDeviceConn: Remote connection setup (RemoteDeviceConnSubmenuDH)
     * - RemoteDeviceClone: Remote peer device controls (DeviceSubmenuDH with remoteKey)
     * 
     * Handler lifecycle:
     * 1. Checks if submenu change needed (currentSubmenu != sub || force)
     * 2. Creates appropriate SubmenuDivHandler instance via switch statement
     * 3. Caches handler in divHandlerMap for future reference
     * 4. Calls handler.displayMenuContent() to render DOM elements
     * 5. Updates currentSubmenu tracker
     * 6. Calls updateConditionals() to sync button states with current state
     * 
     * Note: RemoteDeviceClone handlers are cached by submenuKey to support multiple remote peers
     */
    displayMenuContent(sub: Submenu, force?: boolean, submenuKey?: string) {
        if(this.currentSubmenu != sub || force) {
            let divHandler: SubmenuDivHandler | undefined;
            switch (sub) {
                case Submenu.Main:
                    divHandler = new VidshareSubmenuDH();

                    break;

                case Submenu.Device:
                    divHandler = new DeviceSubmenuDH();

                    break;

                case Submenu.Avatar:
                    divHandler = new AvatarSubmenuDH();

                    break;

                case Submenu.RemoteDeviceConn:
                    divHandler = new RemoteDeviceConnSubmenuDH();

                    break;

                case Submenu.RemoteDeviceClone:
                    Logger.debug(MODULE, "Menu requested for remote devices of peer ID: " + submenuKey);
                    const peerId = submenuKey ? Number(submenuKey) : undefined;
                    divHandler = new DeviceSubmenuDH(true, peerId);

                    break;

                case Submenu.EToy:
                    divHandler = new EToySubmenuDH();

                    break;

                case Submenu.Scene:
                    divHandler = new SceneSubmenuDH();

                    break;

                case Submenu.Settings:
                    divHandler = new SettingsSubmenuDH();

                    break;

                case Submenu.Account:
                    divHandler = new AccountSubmenuDH();

                    break;

                case Submenu.Entertainers:
                    divHandler = new EntertainersSubmenuDH();

                    break;

                case Submenu.Rooms:
                    divHandler = new RoomsSubmenuDH();

                    break;

                case Submenu.UserAdmin:
                    divHandler = new UserAdminSubmenuDH();

                    break;
            }
            if (divHandler) {
                divHandler.displayMenuContent(this.modalContent);
                this.divHandlerMap.set(sub, divHandler);
            }
        }

        this.currentSubmenu = sub;
    }

    private disableXR() {
        if (this.engine.xr && this.engine.xr.session) {
            this.engine.xr.session.end();
        }
    }

    private reenableXR() {
        if (this.wasInXR &&this.latestXRSession) {
            if(this.latestXRSession.environmentBlendMode === 'opaque') {
                this.engine
                    .requestXRSession('immersive-vr', ['local',], ['hand-tracking','hit-test',])
                    .catch((e) => console.error(e));
            } else if (this.latestXRSession && this.latestXRSession.environmentBlendMode === 'additive') {
                this.engine
                    .requestXRSession('immersive-ar', ['local',], ['hand-tracking','hit-test',])
                    .catch((e) => console.error(e));
            }
        }
    }

    displayMenu () {
        const vrMenuRenderer = this.resolveVrMenuRenderer();
        if (this.inXR && vrMenuRenderer) {
            if (vrMenuRenderer.isMenuVisible()) {
                this.hideVrMenu();
                return;
            }

            this.hideMenuContent(this.currentSubmenu);
            this.modal.style.display = 'none';
            let targetSubmenu = this.currentSubmenu;
            const targetSchema = this.getSchemaForSubmenu(targetSubmenu);
            if (targetSchema && !isSchemaVisibleOnPlatform(targetSchema, 'vr')) {
                targetSubmenu = Submenu.Main;
            }

            void vrMenuRenderer.switchVrSubmenu(targetSubmenu);
            vrMenuRenderer.showMenu();
            return;
        }

        // Dropping out of XR to show the HTML menu is a hostile failure; report why instead.
        if (this.inXR) {
            const reason = !this.vrMenuObject
                ? 'vrMenuObject is not assigned on MenuController'
                : 'MenuMesh has no vr-menu-renderer component at runtime';
            Logger.error(MODULE, `Cannot open the VR menu: ${reason}`);
            UserMessaging.instance.notify(`VR menu unavailable: ${reason}`, 10.0);
            return;
        }

        this.wasInXR = false;

        const btnNavMain = document.getElementById('mm-nav-main-button');
        const btnNavDevice = document.getElementById('mm-nav-device-button');
        const btnNavAvatar = document.getElementById('mm-nav-avatar-button');
        const btnNavRemotedvc = document.getElementById('mm-nav-remotecon-button');
        const btnNavEToy = document.getElementById('mm-nav-etoy-button');
        const btnNavScene = document.getElementById('mm-nav-scene-button');
        const btnNavSettings = document.getElementById('mm-nav-settings-button');
        const btnNavAccount = document.getElementById('mm-nav-account-button');
        const btnNavEntertainers = document.getElementById('mm-nav-entertainers-button');
        const btnNavUserAdmin = document.getElementById('mm-nav-useradmin-button');
        const btnNavRooms = document.getElementById('mm-nav-rooms-button');
        const btnClosex = document.getElementById('mm-vidshare-closex-button');
        const footerDiv = document.getElementById('menu-modal-footer-text');

        if (!this.modal || !btnNavMain || !btnNavDevice || !btnNavAvatar || !btnNavRemotedvc || !btnNavEToy || !btnNavScene || !btnNavSettings || !btnNavAccount || !btnNavEntertainers || !btnNavUserAdmin || !btnNavRooms || !btnClosex || !footerDiv) {
            Logger.error(MODULE, 'Menu Controller could not find one or more navigation buttons. These html objects should have been injected by this class at runtime.');
            return;
        }

        this.applySchemaVisibilityToNavButtons({
            btnNavMain,
            btnNavDevice,
            btnNavAvatar,
            btnNavRemotedvc,
            btnNavEToy,
            btnNavScene,
            btnNavSettings,
            btnNavAccount,
            btnNavEntertainers,
            btnNavUserAdmin,
            btnNavRooms,
        });
        this.refreshUserAdminNavVisibility(btnNavUserAdmin as HTMLButtonElement);
        
        this.footerMessageDiv = footerDiv as HTMLDivElement;

        // DEBUG: Menu dimensions logging removed for production

        btnNavMain.addEventListener('click', () => {
            if (this.isWebSubmenuVisible(Submenu.Main) && this.currentSubmenu != Submenu.Main) {
                this.hideMenuContent(this.currentSubmenu);
                this.displayMenuContent(Submenu.Main);
            }
        });
        btnNavDevice.addEventListener('click', () => {
            if (this.isWebSubmenuVisible(Submenu.Device) && this.currentSubmenu != Submenu.Device) {
                this.hideMenuContent(this.currentSubmenu);
                this.displayMenuContent(Submenu.Device);
            }
        });
        btnNavAvatar.addEventListener('click', () => {
            if (this.isWebSubmenuVisible(Submenu.Avatar) && this.currentSubmenu != Submenu.Avatar) {
                this.hideMenuContent(this.currentSubmenu);
                this.displayMenuContent(Submenu.Avatar);
            }
        });
        btnNavRemotedvc.addEventListener('click', () => {
            if (this.isWebSubmenuVisible(Submenu.RemoteDeviceConn) && this.currentSubmenu != Submenu.RemoteDeviceConn) {
                this.hideMenuContent(this.currentSubmenu);
                this.displayMenuContent(Submenu.RemoteDeviceConn);
            }
        });
        btnNavEToy.addEventListener('click', () => {
            if (this.isWebSubmenuVisible(Submenu.EToy) && this.currentSubmenu != Submenu.EToy) {
                this.hideMenuContent(this.currentSubmenu);
                this.displayMenuContent(Submenu.EToy);
            }
        });
        btnNavScene.addEventListener('click', () => {
            if (this.isWebSubmenuVisible(Submenu.Scene) && this.currentSubmenu != Submenu.Scene) {
                this.hideMenuContent(this.currentSubmenu);
                this.displayMenuContent(Submenu.Scene);
            }
        });
        btnNavSettings.addEventListener('click', () => {
            if (this.isWebSubmenuVisible(Submenu.Settings) && this.currentSubmenu != Submenu.Settings) {
                this.hideMenuContent(this.currentSubmenu);
                this.displayMenuContent(Submenu.Settings);
            }
        });
        btnNavAccount.addEventListener('click', () => {
            if (this.isWebSubmenuVisible(Submenu.Account) && this.currentSubmenu != Submenu.Account) {
                this.hideMenuContent(this.currentSubmenu);
                this.displayMenuContent(Submenu.Account);
            }
        });
        btnNavEntertainers.addEventListener('click', () => {
            if (this.isWebSubmenuVisible(Submenu.Entertainers) && this.currentSubmenu != Submenu.Entertainers) {
                this.hideMenuContent(this.currentSubmenu);
                this.displayMenuContent(Submenu.Entertainers);
            }
        });
        btnNavUserAdmin.addEventListener('click', () => {
            if (this.isWebSubmenuVisible(Submenu.UserAdmin) && this.currentSubmenu != Submenu.UserAdmin) {
                this.hideMenuContent(this.currentSubmenu);
                this.displayMenuContent(Submenu.UserAdmin);
            }
        });
        btnNavRooms.addEventListener('click', () => {
            if (this.isWebSubmenuVisible(Submenu.Rooms) && this.currentSubmenu != Submenu.Rooms) {
                this.hideMenuContent(this.currentSubmenu);
                this.displayMenuContent(Submenu.Rooms);
            }
        });
        btnClosex.addEventListener('click', () => {
            this.hideMenu();
        });

        this.displayMenuContent(Submenu.Main, true);

        this.modal.style.display = 'flex';
        this.displayDynamicNavElements();
    }

    private async refreshUserAdminNavVisibility(userAdminButton: HTMLButtonElement): Promise<void> {
        userAdminButton.style.display = 'none';

        const eventsClient = EventsClient.instance;
        if (!eventsClient) {
            return;
        }

        const token = eventsClient.getAuthToken();
        if (!token) {
            return;
        }

        try {
            const resp = await fetch(`${eventsClient.getRestApiBaseUrl()}/api/users/me`, {
                headers: {
                    'Authorization': `Bearer ${token}`,
                },
            });

            if (!resp.ok) {
                return;
            }

            const profile = await resp.json() as { is_user_admin?: boolean };
            if (profile.is_user_admin) {
                userAdminButton.style.display = '';
            }
        } catch {
            // Leave hidden if profile fetch fails.
        }
    }

    /**
     * Gets the MenuSchema for a given submenu.
     * Maps Submenu enum values to their corresponding schema creation functions.
     * Returns null for submenus without defined schemas (e.g., RemoteDeviceClone is dynamic).
     * 
     * @param sub - The submenu type to get schema for
     * @returns MenuSchema if schema exists, null otherwise
     */
    private getSchemaForSubmenu(sub: Submenu): MenuSchema | null {
        switch (sub) {
            case Submenu.Main:
                return createVrMainMenuSchema();
            case Submenu.Device:
                return createDeviceMenuSchema();
            case Submenu.Avatar:
                return createAvatarMenuSchema();
            case Submenu.RemoteDeviceConn:
                return createRemoteDeviceConnMenuSchema();
            case Submenu.EToy:
                return createEToyMenuSchema();
            case Submenu.Scene:
                return createSceneMenuSchema();
            case Submenu.Settings:
                return createSettingsMenuSchema();
            case Submenu.Account:
                return createAccountMenuSchema();
            case Submenu.Entertainers:
                return createEntertainersMenuSchema();
            case Submenu.Rooms:
                return createRoomsMenuSchema();
            case Submenu.UserAdmin:
                return createUserAdminMenuSchema();
            case Submenu.RemoteDeviceClone:
                // RemoteDeviceClone is dynamic, uses DeviceMenuSchema but is always visible
                return null;
            default:
                return null;
        }
    }

    /**
     * Checks if a submenu is visible on the web platform.
     * Uses schema's platforms field: if undefined or includes 'web', the submenu is visible.
     * 
     * @param sub - The submenu type to check visibility for
     * @returns true if submenu should be visible on web, false if explicitly hidden
     */
    private isWebSubmenuVisible(sub: Submenu): boolean {
        const schema = this.getSchemaForSubmenu(sub);
        if (!schema) return true; // Default to visible for dynamic submenus
        return isSchemaVisibleOnPlatform(schema, 'web');
    }

    /**
     * Applies schema-driven platform visibility to all HTML nav buttons.
     * Hides buttons for submenus that don't include 'web' in their platforms field.
     * 
     * @param buttons - Map of button elements by their logical names
     */
    private applySchemaVisibilityToNavButtons(buttons: {
        btnNavMain: Element | null;
        btnNavDevice: Element | null;
        btnNavAvatar: Element | null;
        btnNavRemotedvc: Element | null;
        btnNavEToy: Element | null;
        btnNavScene: Element | null;
        btnNavSettings: Element | null;
        btnNavAccount: Element | null;
        btnNavEntertainers: Element | null;
        btnNavUserAdmin: Element | null;
        btnNavRooms: Element | null;
    }): void {
        const visibility: Array<[Element | null, Submenu]> = [
            [buttons.btnNavMain, Submenu.Main],
            [buttons.btnNavDevice, Submenu.Device],
            [buttons.btnNavAvatar, Submenu.Avatar],
            [buttons.btnNavRemotedvc, Submenu.RemoteDeviceConn],
            [buttons.btnNavEToy, Submenu.EToy],
            [buttons.btnNavScene, Submenu.Scene],
            [buttons.btnNavSettings, Submenu.Settings],
            [buttons.btnNavAccount, Submenu.Account],
            [buttons.btnNavEntertainers, Submenu.Entertainers],
            [buttons.btnNavUserAdmin, Submenu.UserAdmin],
            [buttons.btnNavRooms, Submenu.Rooms],
        ];

        for (const [button, submenu] of visibility) {
            if (!button) continue;
            const isVisible = this.isWebSubmenuVisible(submenu);
            (button as HTMLElement).style.display = isVisible ? '' : 'none';
        }
    }

    /**
     * Dynamically generates and displays navigation buttons for remote device control.
     * Creates a button for each connected remote peer, showing their username.
     * 
     * @remarks
     * - Only generates buttons when menu modal is visible (display === 'flex')
     * - Buttons are cached in remoteDeviceNavButtons map to avoid duplicates
     * - Each button switches view to RemoteDeviceClone submenu for that peer
     * - Button labels use remote usernames from EventsClient for readability
     * - Buttons persist until explicitly removed via removeDynamicSubmenu()
     * 
     * Called when:
     * - New remote connection established (IntifaceController.onRemoteConnected)
     * - Menu is opened while remote connections exist
     * - Device control permissions are granted/revoked
     */
    public displayDynamicNavElements() {
        if(this.modal.style.display == 'flex') {
            const remoteUserIDs: number[] = IntifaceController.instance.getControllableUsers();
            const navModal: HTMLDivElement = document.getElementById('menu-modal-navigation') as HTMLDivElement;

            for (let userID of remoteUserIDs) {
                if (!this.remoteDeviceNavButtons.has(userID)) {
                    const newNavButton = document.createElement('input');
                    newNavButton.type = 'button';
                    newNavButton.classList.add('mm-nav-button');
                    newNavButton.value = EventsClient.instance.remoteUsernames.get(userID) || "headless";
                    newNavButton.id = 'mm-nav-remote-control-' + userID;

                    newNavButton.addEventListener('click', () => {
                        this.hideMenuContent(this.currentSubmenu);
                        this.displayMenuContent(Submenu.RemoteDeviceClone, true, userID.toString());
                    });

                    navModal.appendChild(newNavButton);
                    this.remoteDeviceNavButtons.set(userID, newNavButton);
                }
            }     
        }
    }

    /**
     * Removes dynamic navigation button and handles view switching when remote peer disconnects.
     * Cleans up UI elements and navigates away from disconnected peer's submenu if active.
     * 
     * @param keyOrId - Either the temporary negotiation key (string) or numeric peer ID
     * @param peerId - Optional numeric peer ID for navigation button lookup
     * 
     * @remarks
     * Cleanup operations:
     * 1. Removes navigation button from DOM using peerId lookup
     * 2. Deletes button from remoteDeviceNavButtons cache
     * 3. Checks if currently viewing this peer's RemoteDeviceClone submenu
     * 4. If viewing disconnected peer, switches back to RemoteDeviceConn submenu
     * 
     * Key matching logic:
     * - Matches against currentSubmenuKey using both string and numeric forms
     * - Handles both temporary negotiation keys and established peer IDs
     * - Falls back to RemoteDeviceConn if no submenu key found (safety fallback)
     * 
     * @example
     * // Called when remote peer disconnects
     * MenuController.instance.removeDynamicSubmenu("temp-key-123", 42);
     * // Removes button for peer 42 and switches view if currently active
     */
    public removeDynamicSubmenu(keyOrId: string | number, peerId?: number) {
        // Remove navigation button by peerId if available
        if (peerId !== undefined) {
            const navButton = this.remoteDeviceNavButtons.get(peerId);
            if (navButton) {
                navButton.remove();
                this.remoteDeviceNavButtons.delete(peerId);
            }
        }

        // If we're currently viewing this remote device submenu, switch back to RemoteDeviceConn
        const currentSubmenuKey = this.getCurrentSubmenuKey();
        
        // Check if the current submenu key matches either the peerKey string or peerId
        const keyMatches = currentSubmenuKey === keyOrId.toString() || 
                          (peerId !== undefined && currentSubmenuKey === peerId.toString());
        
        // If viewing ANY RemoteDeviceClone submenu and the keys match, switch back
        if (this.currentSubmenu === Submenu.RemoteDeviceClone && (keyMatches || !currentSubmenuKey)) {
            this.hideMenuContent(Submenu.RemoteDeviceClone);
            this.displayMenuContent(Submenu.RemoteDeviceConn, true);
        }
    }

    private getCurrentSubmenuKey(): string | undefined {
        const divHandler = this.divHandlerMap.get(this.currentSubmenu);
        if (divHandler && divHandler instanceof DeviceSubmenuDH) {
            return divHandler.peerId?.toString();
        }
        return undefined;
    }

    hideMenuContent(sub: Submenu) {
        if(this.currentSubmenu == sub) {
            if (sub === Submenu.Avatar) {
                this.clearAvatarPreview();
            }
            this.divHandlerMap.get(sub)?.hideContent();
            this.divHandlerMap.delete(sub);

            this.modalContent.innerHTML = "";
        }
    }

    hideMenu(restoreXR: boolean = true) {
        const vrMenuRenderer = this.resolveVrMenuRenderer();
        if (this.inXR && vrMenuRenderer) {
            this.hideVrMenu();
            return;
        }

        const btnClosex = document.getElementById('mm-vidshare-closex-button');

        //Clone buttons and replace them. This gets rid of all current event listeners
        var newBtn = btnClosex?.cloneNode(true);
        if(newBtn) {
            btnClosex?.parentNode?.replaceChild(newBtn, btnClosex);
        }

        //this.remoteDeviceNavButtons.clear();

        this.hideMenuContent(this.currentSubmenu);

        this.modal.style.display = 'none';

        if(restoreXR && this.wasInXR) {
            this.reenableXR();
        }
    }

    private resolveAvatarTemplateRoot(): Object3D | null {
        return AvatarController.instance?.templateRoot ?? null;
    }

    private resolveVrMenuRenderer(): VrMenuRenderer | null {
        if (this.vrMenuRenderer) {
            return this.vrMenuRenderer;
        }

        const vrObject = this.vrMenuObject;
        if (!vrObject) {
            return null;
        }

        const renderer = vrObject.getComponent(VrMenuRenderer);
        if (!renderer) {
            // Falls back to the registered type name in case the class identity differs from the scene's.
            const byTypeName = vrObject.getComponent('vr-menu-renderer') as VrMenuRenderer | null;
            if (byTypeName) {
                Logger.warn(MODULE, 'VrMenuRenderer resolved by type name; the imported class did not match.');
                this.vrMenuRenderer = byTypeName;
                return byTypeName;
            }
            Logger.warn(MODULE, 'vrMenuObject does not have a VrMenuRenderer component yet.');
            return null;
        }

        this.vrMenuRenderer = renderer;
        return renderer;
    }

    private hideVrMenu(): void {
        const vrMenuRenderer = this.resolveVrMenuRenderer();
        if (vrMenuRenderer) {
            vrMenuRenderer.hideMenu();
        }
    }

    private syncVrPreAuthMenuVisibility(): void {
        const vrMenuRenderer = this.resolveVrMenuRenderer();
        if (!vrMenuRenderer) {
            return;
        }

        if (!this.inXR) {
            if (vrMenuRenderer.isPreAuthMenuActive()) {
                vrMenuRenderer.hidePreAuthMenu();
            }
            return;
        }

        const preAuthState = EventsClient.instanceOrNull?.getVrPreAuthMenuState() ?? null;
        if (!preAuthState) {
            if (vrMenuRenderer.isPreAuthMenuActive()) {
                vrMenuRenderer.hidePreAuthMenu();
            }
            return;
        }

        this.hideMenuContent(this.currentSubmenu);
        this.modal.style.display = 'none';
        vrMenuRenderer.showPreAuthMenu(preAuthState);
    }

    /**
     * Returns true when the in-world VR menu is currently visible.
     * Used by UserMessaging to avoid duplicating the same message in HUD and menu footer.
     */
    public isVrMenuActive(): boolean {
        const vrMenuRenderer = this.resolveVrMenuRenderer();
        return !!vrMenuRenderer?.isMenuVisible();
    }

    public clearAvatarPreview(): void {
        if (this.avatarPreviewInstance && !this.avatarPreviewInstance.isDestroyed) {
            this.avatarPreviewInstance.destroy();
        }
        this.avatarPreviewInstance = null;
    }

    /** The VR panel lives in world space, so it needs its own anchor; falls back to the web one. */
    private resolveAvatarPreviewPane(): Object3D | null {
        if (this.inXR) {
            return this.avatarPreviewPaneVR ?? this.avatarPreviewPane;
        }
        return this.avatarPreviewPane;
    }

    private removeDeactivateOnStartComponents(root: Object3D): void {
        for (const component of root.getComponents()) {
            if ((component as any).type === 'deactivate-on-start') {
                component.destroy();
            }
        }

        for (const child of root.children) {
            this.removeDeactivateOnStartComponents(child);
        }
    }

    public showAvatarPreview(avatarId: string): boolean {
        const previewPane = this.resolveAvatarPreviewPane();
        const templateRoot = this.resolveAvatarTemplateRoot();
        if (!previewPane || previewPane.isDestroyed) {
            Logger.warn(MODULE, 'avatarPreviewPane is missing; cannot render avatar preview.');
            return false;
        }
        if (!templateRoot || templateRoot.isDestroyed) {
            Logger.warn(MODULE, 'AvatarController avatarTemplates is missing; cannot render avatar preview.');
            return false;
        }

        const avatar = getAvatarEntryById(avatarId);
        const template = templateRoot.findByNameRecursive(avatar.templateName)[0];
        if (!template) {
            Logger.warn(MODULE, `Avatar preview template not found: ${avatar.templateName}`);
            return false;
        }

        this.clearAvatarPreview();
        const clone = template.clone(null);
        if (!clone) {
            Logger.warn(MODULE, `Failed to clone avatar preview template: ${avatar.templateName}`);
            return false;
        }

        this.removeDeactivateOnStartComponents(clone);
        setComponentsActive(clone, true);

        clone.parent = previewPane;
        clone.rotateAxisAngleDegObject([0, 1, 0], 180);
        this.avatarPreviewInstance = clone;
        return true;
    }

    update(dt: number) {
        if (this.modal.style.display == 'flex') {
            this.updateConditionals();
        } else {

            if (this.inXR) {
                const xr = this.engine.xr;

                if (this.ignoreButtonInput) {
                    let anypressed = false;
                    if (xr && (xr as any).session) {
                        for (const inputSource of (xr as any).session.inputSources) {
                            const gamepad = inputSource.gamepad;
                            if (!gamepad) continue;

                            for (let i: number = 0; i < gamepad.buttons.length; i++) {
                                if (gamepad.buttons[i].pressed) {
                                    anypressed = true;
                                    break;
                                }
                            }
                            if (anypressed) break;
                        }
                    }

                    if (!anypressed) {
                        this.ignoreButtonInput = false;
                        this.xrMenuButtonWasPressed = false;
                    }
                } else {
                    if (xr && (xr as any).session) {
                        let leftMenuPressed = false;
                        for (const inputSource of (xr as any).session.inputSources) {
                            const gamepad = inputSource.gamepad;
                            if (!gamepad) continue;

                            /* Uncomment this for loop for debugging

                            for (let i: number = 0; i < gamepad.buttons.length; i++) {
                                if (gamepad.buttons[i].pressed) {
                                    Logger.debug(MODULE, "VR Button " + i + " pressed, handedness: " + inputSource.handedness);
                                }
                            }

                            */

                            if (inputSource.gamepad && inputSource.handedness === "left") {
                                if (gamepad.buttons[4].pressed) {
                                    leftMenuPressed = true;
                                }
                            }
                        }

                        if (leftMenuPressed && !this.xrMenuButtonWasPressed) {
                            this.displayMenu();
                        }
                        this.xrMenuButtonWasPressed = leftMenuPressed;
                    } else {
                        this.xrMenuButtonWasPressed = false;
                    }
                }
            } else if (this.ignoreButtonInput) {
                this.ignoreButtonInput = false;
                this.xrMenuButtonWasPressed = false;
            } else {
                this.xrMenuButtonWasPressed = false;
            }
        }
    }

    /**
     * Refreshes device control UI for the currently active device submenu.
     * Updates range sliders, buttons, and state displays to reflect current device states.
     * 
     * @param force - Force update regardless of current submenu (updates Device submenu)
     * 
     * @remarks
     * Conditional update logic:
     * - If current submenu is Device OR force is true: updates local device controls
     * - If current submenu is RemoteDeviceClone: updates remote device controls for active peer
     * - Otherwise: no update performed (optimization to avoid unnecessary DOM manipulation)
     * 
     * Called when:
     * - Device added/removed from Buttplug/Intiface
     * - Device command executed (to show updated values)
     * - Timed command completes (to clear state)
     * - Connection state changes (connect/disconnect)
     * - Remote peer updates device list
     * 
     * Performance note: Only updates active submenu to minimize DOM operations
     */
    updateDeviceControls(force? : boolean) {
        Logger.debug(MODULE, "Updating device controls. Current submenu: " + this.currentSubmenu);
        if(this.currentSubmenu == Submenu.Device || force) {
            (this.divHandlerMap.get(Submenu.Device) as DeviceSubmenuDH)?.updateDeviceControls();
        } else if (this.currentSubmenu == Submenu.RemoteDeviceClone) {
            (this.divHandlerMap.get(Submenu.RemoteDeviceClone) as DeviceSubmenuDH)?.updateDeviceControls();
        }
    }

    /**
     * Displays a timestamped message in the menu footer.
     * Used for error messages, status updates, and user feedback.
     * 
     * @param message - Text message to display to user
     * 
     * @remarks
     * Format: "[MM/DD/YY, HH:MM:SS AM/PM] message"
     * 
     * Uses Intl.DateTimeFormat for localized timestamp formatting:
     * - Locale: en-US (12-hour clock with AM/PM)
     * - Format: 2-digit year, month, day, hour, minute, second
     * 
     * Common use cases:
     * - Device connection errors: "Device 0 not found or disconnected"
     * - WebRTC connection failures: "Could not connect to remote peer with key: xyz"
     * - Command validation errors: "No actuation values provided"
     * - Status updates: "Connected with peerID: 42"
     * 
     * Message persists until next call (no auto-clear)
     * 
     * @example
     * MenuController.instance.displayFooterMessage("Device connected successfully");
     * // Displays: "[12/03/24, 02:30:45 PM] Device connected successfully"
     */
    public displayFooterMessage(message: string) {
        const vrMenuRenderer = this.resolveVrMenuRenderer();
        if (vrMenuRenderer) {
            vrMenuRenderer.setFooterMessage(message);
        }

        if (!this.footerMessageDiv || typeof this.footerMessageDiv.innerText !== 'string') {
            return;
        }


        this.footerMessageDiv.innerText = message;
    }
}
